import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Check, Share2, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { t, language } = useLanguage();
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [preorders, setPreorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    country: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all user's preorders
        const response = await axios.get(`${API}/preorders/me`, { withCredentials: true });
        if (response.data) {
          setPreorders(Array.isArray(response.data) ? response.data : [response.data]);
        }
      } catch (error) {
        console.error("Failed to fetch preorders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      // Initialize form with user data or preorder data
      const latestPreorder = preorders[0];
      setFormData({
        name: user.name || "",
        phone: user.phone || latestPreorder?.phone || "",
        address: user.address || latestPreorder?.address || "",
        city: user.city || latestPreorder?.city || "",
        postal_code: user.postal_code || latestPreorder?.postal_code || "",
        country: user.country || latestPreorder?.country || ""
      });
    }
  }, [user, preorders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, formData, { withCredentials: true });
      await checkAuth();
      alert(language === 'fr' ? "Profil mis à jour !" : "Profile updated!");
    } catch (error) {
      console.error("Failed to save profile:", error);
      alert(language === 'fr' ? "Erreur lors de la sauvegarde" : "Error saving profile");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = () => {
    const text = t("dashboard_share_text");
    const url = window.location.origin;
    if (navigator.share) {
      navigator.share({ title: "Calli", text, url });
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
      alert(language === 'fr' ? "Lien copié !" : "Link copied!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-calli">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-calli pt-20 pb-12" data-testid="dashboard">
      <div className="calli-container">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border-2 border-orange-200" />
              ) : (
                <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-yellow-400 rounded-full flex items-center justify-center text-white text-xl font-bold border-2 border-orange-200">
                  {user?.name?.charAt(0) || "U"}
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold">{user?.name}</h1>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/")}
              className="text-orange-600 hover:underline text-sm"
            >
              ← {language === 'fr' ? 'Accueil' : 'Home'}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { id: "profile", label: language === 'fr' ? "Profil" : "Profile", icon: "👤" },
              { id: "orders", label: language === 'fr' ? "Commandes" : "Orders", icon: "📦" },
              { id: "address", label: language === 'fr' ? "Adresse" : "Address", icon: "📍" },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? "bg-orange-500 text-white shadow-lg" 
                    : "bg-white text-gray-600 hover:bg-orange-50"
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
              <h2 className="text-lg font-bold flex items-center gap-2">
                👤 {language === 'fr' ? 'Informations personnelles' : 'Personal information'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'fr' ? 'Nom complet' : 'Full name'}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'fr' ? 'Téléphone' : 'Phone'}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="w-full px-4 py-3 border border-gray-100 rounded-xl bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {language === 'fr' ? "L'email ne peut pas être modifié" : "Email cannot be changed"}
                  </p>
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? "..." : (language === 'fr' ? "💾 Sauvegarder" : "💾 Save")}
              </button>

              {/* Share & Logout */}
              <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={handleShare}
                  className="text-sm text-orange-600 hover:underline flex items-center gap-1"
                >
                  <Share2 className="w-4 h-4" />
                  {language === 'fr' ? 'Partager Calli' : 'Share Calli'}
                </button>
                <button
                  onClick={logout}
                  className="text-sm text-gray-400 hover:text-red-500 flex items-center gap-1"
                >
                  <LogOut className="w-4 h-4" />
                  {language === 'fr' ? 'Déconnexion' : 'Logout'}
                </button>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
                  📦 {language === 'fr' ? 'Mes commandes' : 'My orders'}
                </h2>
                
                {preorders.length > 0 ? (
                  <div className="space-y-4">
                    {preorders.map((order, index) => (
                      <div key={index} className="border border-gray-100 rounded-xl p-4 hover:border-orange-200 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <Check className="w-4 h-4 text-green-600" />
                              </span>
                              <div>
                                <p className="font-bold text-green-600">
                                  {language === 'fr' ? 'Précommande confirmée' : 'Preorder confirmed'}
                                </p>
                                <p className="text-xs text-gray-400">#{order.preorder_id}</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-2xl font-black text-orange-500">{order.amount}€</p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-gray-500 text-xs">{language === 'fr' ? 'Date' : 'Date'}</p>
                            <p className="font-medium">
                              {new Date(order.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric', month: 'short', year: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-3">
                            <p className="text-gray-500 text-xs">{language === 'fr' ? 'Livraison prévue' : 'Expected delivery'}</p>
                            <p className="font-medium text-orange-600">{t("dashboard_delivery_date")}</p>
                          </div>
                        </div>
                        
                        {order.address && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500 mb-1">{language === 'fr' ? 'Livraison à :' : 'Shipping to:'}</p>
                            <p className="text-sm text-gray-700">
                              {order.address}, {order.postal_code} {order.city}, {order.country}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">📦</span>
                    </div>
                    <p className="text-gray-500 mb-4">
                      {language === 'fr' ? "Vous n'avez pas encore de commande" : "You don't have any orders yet"}
                    </p>
                    <button
                      onClick={() => navigate("/")}
                      className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold px-6 py-3 rounded-xl hover:opacity-90"
                    >
                      {language === 'fr' ? '🚀 Précommander maintenant' : '🚀 Preorder now'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === "address" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
              <h2 className="text-lg font-bold flex items-center gap-2">
                📍 {language === 'fr' ? 'Adresse de livraison' : 'Shipping address'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'fr' ? 'Adresse' : 'Address'}
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    placeholder={language === 'fr' ? '123 Rue de la Paix' : '123 Main Street'}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === 'fr' ? 'Code postal' : 'Postal code'}
                    </label>
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                      placeholder="75001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === 'fr' ? 'Ville' : 'City'}
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                      placeholder="Paris"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'fr' ? 'Pays' : 'Country'}
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    placeholder="France"
                  />
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? "..." : (language === 'fr' ? "💾 Sauvegarder l'adresse" : "💾 Save address")}
              </button>
              
              <p className="text-xs text-gray-400 text-center">
                {language === 'fr' 
                  ? "Cette adresse sera utilisée pour vos prochaines commandes" 
                  : "This address will be used for your next orders"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
